import * as React from "react";
import type {
  PowerBacktestCandidate,
  PowerDemandSliceResult,
  PowerEstimate,
  PowerForecastPoint,
} from "../../models/powerDemand";

export interface PowerDemandPanelProps {
  result: PowerDemandSliceResult;
  /** Controlled by the product's only first-level navigation: monitor / forecast. */
  mode: "monitor" | "forecast";
}

function value(estimate: PowerEstimate) {
  return `${estimate.value.toFixed(2)} ${estimate.unit}`;
}

function range(estimate: PowerEstimate) {
  return `${estimate.low.toFixed(2)}–${estimate.high.toFixed(2)} ${estimate.unit}`;
}

function MethodDisclosure({ result }: { result: PowerDemandSliceResult }) {
  return (
    <details className="sheet limitation-sheet">
      <summary className="sheet-heading compact">
        <span className="section-kicker">METHOD &amp; SOURCE</span>
        <strong>物理路径、回测口径与数据来源</strong>
      </summary>
      <div className="method-grid">
        <div><span>部署与存量</span><p>{result.methodology.physicalPath}</p></div>
        <div><span>IT overhead</span><p>{result.methodology.overhead}</p></div>
        <div><span>GW → TWh</span><p>{result.methodology.energyConversion}</p></div>
        <div><span>不确定性</span><p>{result.methodology.uncertainty}</p></div>
        <div><span>rolling-origin</span><p>{result.methodology.backtest}</p></div>
        <div><span>Scenario</span><p>{result.methodology.scenarioPolicy}</p></div>
        <div><span>Frontier 约束</span><p>{result.methodology.frontierPolicy}</p></div>
      </div>
      <div className="table-shell">
        <table className="research-table source-table">
          <caption>可追溯数据源</caption>
          <thead><tr><th>来源</th><th>As of</th><th>用途</th></tr></thead>
          <tbody>{result.sources.map((source) => (
            <tr key={source.id}>
              <td><a href={source.url} target="_blank" rel="noreferrer">{source.label}</a></td>
              <td>{source.asOf}</td>
              <td>{source.role}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </details>
  );
}

function MonitorView({ result }: { result: PowerDemandSliceResult }) {
  const monitor = result.monitor;
  return (
    <div className="workspace-view power-demand-panel">
      <section className="sheet" aria-labelledby="power-demand-monitor-heading">
        <div className="sheet-heading">
          <div>
            <div className="section-kicker">REAL-TIME DATA MONITOR / POWER</div>
            <h2 id="power-demand-monitor-heading">AI 电力需求监控</h2>
            <p>共同水位 {monitor.epochCoverage.commonWatermark}；观测站点与物理估算分开展示。</p>
          </div>
          <div className="forecast-summary">
            <span>Facility Load · 物理估算</span>
            <strong>{value(monitor.facilityLoadGw)}</strong>
            <small>{range(monitor.facilityLoadGw)}</small>
            <b>季度耗电 {value(monitor.quarterlyTwh)}</b>
          </div>
        </div>
        <div className="diagnostic-strip" aria-label="AI 电力需求当期摘要">
          <div><span>IT 铭牌功率</span><strong>{value(monitor.itNameplateGw)}</strong><small>物理估算 · {range(monitor.itNameplateGw)}</small></div>
          <div><span>估算实际 IT Load</span><strong>{value(monitor.itLoadGw)}</strong><small>利用率作用后</small></div>
          <div><span>Facility Load</span><strong>{value(monitor.facilityLoadGw)}</strong><small>PUE 作用后</small></div>
          <div><span>季度耗电</span><strong>{value(monitor.quarterlyTwh)}</strong><small>年化 {value(monitor.annualizedTwh)}</small></div>
        </div>
      </section>

      <section className="sheet" aria-labelledby="power-demand-lineage-heading">
        <div className="sheet-heading compact">
          <div>
            <div className="section-kicker">OBSERVATION / ESTIMATE LINEAGE</div>
            <h3 id="power-demand-lineage-heading">观测、估算与覆盖</h3>
            <p>{monitor.epochCoverage.sourceCoverageWarning}</p>
          </div>
        </div>
        <div className="diagnostic-strip">
          <div><span>Epoch 覆盖站点</span><strong>{monitor.epochCoverage.coveredSites}</strong><small>观测 IT 铭牌 {monitor.epochCoverage.observedItNameplateGw.toFixed(2)} GW</small></div>
          <div><span>Epoch 设施铭牌</span><strong>{monitor.epochCoverage.observedFacilityNameplateGw.toFixed(2)} GW</strong><small>最新观测 {monitor.epochCoverage.sourceLatestObservedDate}</small></div>
          <div><span>加速器铭牌存量</span><strong>{value(monitor.acceleratorNameplateGw)}</strong><small>物理估算</small></div>
          <div><span>Network / CPU overhead</span><strong>{value(monitor.itOverheadGw)}</strong><small>单列，不隐入加速器功率</small></div>
        </div>
        <p className="data-caveat">
          数据质量 · 芯片功率当期覆盖 {monitor.quality.computeCoveragePct.toFixed(0)}% ·
          Epoch timeline {monitor.quality.epochTimelineRows} 条 · 排除未来 projection {monitor.quality.epochProjectedRowsExcluded} 条。
        </p>
      </section>
      <MethodDisclosure result={result} />
    </div>
  );
}

function HorizonCard({ label, point }: { label: string; point: PowerForecastPoint }) {
  return (
    <div>
      <span>{label} · {point.period}</span>
      <strong>{value(point.facilityLoadGw)}</strong>
      <small>季度 {value(point.quarterlyTwh)} · {range(point.facilityLoadGw)}</small>
    </div>
  );
}

function BacktestRow({ model }: { model: PowerBacktestCandidate }) {
  const coverage = model.full.intervalCoverage90Pct == null ? "样本不足" : `${model.full.intervalCoverage90Pct.toFixed(1)}%`;
  return (
    <tr>
      <td><strong>{model.label}</strong><small>{model.full.observations} 个共同 OOS 季度</small></td>
      <td>{model.full.rmse.toFixed(3)} GW</td>
      <td>{model.recent8.rmse.toFixed(3)} GW</td>
      <td>{model.full.mape.toFixed(1)}%</td>
      <td>{coverage}<small>{model.full.intervalObservations} 个可评估区间</small></td>
    </tr>
  );
}

function ForecastView({ result }: { result: PowerDemandSliceResult }) {
  const forecast = result.forecast;
  const impact = forecast.scenarioImpact;
  return (
    <div className="workspace-view power-demand-panel">
      <section className="sheet" aria-labelledby="power-demand-forecast-heading">
        <div className="sheet-heading">
          <div>
            <div className="section-kicker">FORECAST / {forecast.originPeriod}</div>
            <h2 id="power-demand-forecast-heading">AI 电力需求预测</h2>
            <p>芯片交付 → 部署存量 → IT Load → Facility Load → TWh；历史回测锁定。</p>
          </div>
          <div className="forecast-summary">
            <span>8Q Facility Load · 预测</span>
            <strong>{value(forecast.horizons.quarter8.facilityLoadGw)}</strong>
            <small>{range(forecast.horizons.quarter8.facilityLoadGw)}</small>
            <b>{forecast.status === "research-baseline" ? "研究基线" : forecast.status}</b>
          </div>
        </div>
        <div className="diagnostic-strip" aria-label="1Q 4Q 8Q 电力预测">
          <HorizonCard label="1Q" point={forecast.horizons.quarter1} />
          <HorizonCard label="4Q" point={forecast.horizons.quarter4} />
          <HorizonCard label="8Q" point={forecast.horizons.quarter8} />
          <div><span>Scenario 影响</span><strong>{impact.active ? "已启用" : "Baseline"}</strong><small>4Q Δ {impact.quarter4FacilityLoadDeltaGw.toFixed(2)} GW · 8Q Δ {impact.quarter8FacilityLoadDeltaGw.toFixed(2)} GW</small></div>
        </div>
      </section>

      <section className="sheet" aria-labelledby="power-demand-assumptions-heading">
        <div className="sheet-heading compact">
          <div>
            <div className="section-kicker">SCENARIO ENGINE / DIRECT CHANNELS</div>
            <h3 id="power-demand-assumptions-heading">未来假设的物理传导</h3>
            <p>参数只改变未来路径；历史 monitor 和 rolling-origin 回测不会被重算。</p>
          </div>
        </div>
        <div className="diagnostic-strip">
          <div><span>部署滞后</span><strong>{forecast.scenario.deploymentLagQuarters}Q</strong><small>直接影响已部署 IT 铭牌</small></div>
          <div><span>利用率</span><strong>{(forecast.scenario.utilization * 100).toFixed(0)}%</strong><small>直接影响实际 IT Load</small></div>
          <div><span>PUE</span><strong>{forecast.scenario.pue.toFixed(2)}×</strong><small>直接影响 Facility Load / TWh</small></div>
          <div><span>历史回测</span><strong>锁定</strong><small>{impact.historicalBacktestLocked ? "Scenario 不回写历史" : "异常"}</small></div>
        </div>
      </section>

      <section className="sheet" aria-labelledby="power-demand-backtest-heading">
        <div className="sheet-heading compact">
          <div>
            <div className="section-kicker">ROLLING-ORIGIN VALIDATION</div>
            <h3 id="power-demand-backtest-heading">rolling-origin 回测</h3>
            <p>全窗口和近期 8 季同时展示；{result.backtest.statusReason}</p>
          </div>
        </div>
        <div className="table-shell">
          <table className="research-table">
            <thead><tr><th>模型</th><th>全窗 RMSE</th><th>近 8Q RMSE</th><th>全窗 MAPE</th><th>90% 区间覆盖</th></tr></thead>
            <tbody>{Object.values(result.backtest.models).map((model) => <BacktestRow key={model.id} model={model} />)}</tbody>
          </table>
        </div>
      </section>
      <MethodDisclosure result={result} />
    </div>
  );
}

/** Presentational adapter for the product's existing Monitor and Forecast tabs. */
export function PowerDemandPanel({ result, mode }: PowerDemandPanelProps) {
  return mode === "monitor" ? <MonitorView result={result} /> : <ForecastView result={result} />;
}
